import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BidSavedSearchService } from 'src/app/services/bid-saved-search/bid-saved-search.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

interface SavedSearch {
  _id:  string;
  name: string;
  data: Record<string, any>;
  user_id: string;
  user: {
    title: string | null;
    name: string;
    country: string | null;
    city: string | null;
  };
  created_at: {
    $date: string;
  };
  doc_id: string;
}

@Component({
  selector: 'app-saved-searches',
  templateUrl: './saved-searches.component.html',
  styleUrls: ['./saved-searches.component.scss']
})
export class SavedSearchesComponent extends DestructorComponent implements OnInit {

  // Data properties
  savedSearches: SavedSearch[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  currentPage: number = 1;
  searchTerm: string = '';

  // Dynamic columns from data object
  dataColumns: string[] = [];

  // Bulk delete properties
  selectedIds: Set<string> = new Set<string>();
  isDeleting: boolean = false;

  constructor(
    private _bidSavedSearchService: BidSavedSearchService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadSavedSearches();
  }

  loadSavedSearches(page: number = 1, search: string = ''): void {
    this.isLoading = true;
    this.currentPage = page;
    this.searchTerm = search;

    this.subscriptions.push(
      this._bidSavedSearchService.GetData(page.toString(), search).subscribe({
        next: (response: PaginatedResponse<SavedSearch>) => {
          if (response.status && response.data) {
            this.savedSearches = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;

            // Extract unique columns from all data objects
            this.extractDataColumns();
          } else {
            this.savedSearches = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading saved searches:', error);
          this.savedSearches = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private extractDataColumns(): void {
    const columnsSet = new Set<string>();

    this.savedSearches.forEach(search => {
      if (search.data) {
        Object.keys(search.data).forEach(key => columnsSet.add(key));
      }
    });

    this.dataColumns = Array.from(columnsSet);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadSavedSearches(page, this.searchTerm);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadSavedSearches(1, searchTerm);
  }

  trackByFn(_index: number, item: SavedSearch): string {
    return item._id;
  }

  formatDate(dateObj: { $date: string } | string): string {
    let dateString: string;

    if (typeof dateObj === 'object' && dateObj.$date) {
      dateString = dateObj.$date;
    } else if (typeof dateObj === 'string') {
      dateString = dateObj;
    } else {
      return 'N/A';
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  }

  getDataValue(search: SavedSearch, column: string): string {
    return search.data && search.data[column] ? search.data[column] : '-';
  }

  onSearchStock(search: SavedSearch): void {
    // Navigate to search-bids listing page with the saved search data as query params
    const queryParams: any = {};

    if (search.data) {
      // Convert the saved search data to query parameters
      Object.keys(search.data).forEach(key => {
        const value = search.data[key];
        if (value) {
          // Use the key as-is for query params
          queryParams[key] = value;
        }
      });
    }

    // Navigate to the search-bids listing page with query params
    this.router.navigate(['/bid-manager/search-bids/listing'], {
      queryParams: queryParams
    });
  }

  // Bulk delete methods
  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  toggleSelectAll(): void {
    // When using click event, the checked state hasn't changed yet, so we check the opposite
    const willBeChecked = !this.isAllSelected;

    if (willBeChecked) {
      this.savedSearches.forEach(search => {
        this.selectedIds.add(search._id);
      });
    } else {
      this.selectedIds.clear();
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  get isAllSelected(): boolean {
    return this.savedSearches.length > 0 &&
           this.savedSearches.every(search => this.selectedIds.has(search._id));
  }

  get hasSelectedItems(): boolean {
    return this.selectedIds.size > 0;
  }

  onBulkDelete(): void {
    if (this.selectedIds.size === 0) {
      return;
    }

    const count = this.selectedIds.size;
    const itemText = count > 1 ? 'saved searches' : 'saved search';

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${count} ${itemText}. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    this.isDeleting = true;
    const idsArray = Array.from(this.selectedIds);

    this.subscriptions.push(
      this._bidSavedSearchService.BulkDelete(idsArray).subscribe({
        next: (response) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'The saved searches have been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn fw-bold btn-primary'
              }
            });
            this.selectedIds.clear();
            this.loadSavedSearches(this.currentPage, this.searchTerm);
          } else {
            console.error('Bulk delete failed:', response);
            Swal.fire({
              title: 'Error!',
              text: 'Failed to delete saved searches. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn fw-bold btn-primary'
              }
            });
          }
          this.isDeleting = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error during bulk delete:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while deleting saved searches. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn fw-bold btn-primary'
            }
          });
          this.isDeleting = false;
          this.cdr.detectChanges();
        }
      })
    );
  }
}
